import { useEffect, useReducer, useState } from 'react';
import isEqual from 'lodash/isEqual';

import type { Dispatch, Reducer, ReducerAction, ReducerState, SetStateAction } from 'react';

type GenericReducer<T> = Reducer<T, { type: 'ALL'; payload: T } | { type: keyof T; payload: T[keyof T] }>;

export function useOptimisticState<T>(sourceOfTruth: T): [T, Dispatch<SetStateAction<T>>] {
  const [optimisticState, setOptimisticState] = useState<T>(sourceOfTruth);

  useEffect(() => {
    if (sourceOfTruth !== optimisticState) {
      setOptimisticState(sourceOfTruth);
    }
  }, [sourceOfTruth]);

  return [optimisticState, setOptimisticState];
}

export function useOptimisticReducer<T>(
  sourceOfTruth: T,
): [ReducerState<GenericReducer<T>>, Dispatch<ReducerAction<GenericReducer<T>>>] {
  const [optimisticState, setOptimisticState] = useReducer((state: T, { type, payload }) => {
    if (type === 'ALL') {
      return payload;
    }
    return { ...state, [type]: payload };
  }, sourceOfTruth);

  useEffect(() => {
    if (!isEqual(sourceOfTruth, optimisticState)) {
      setOptimisticState({ type: 'ALL', payload: sourceOfTruth });
    }
  }, [sourceOfTruth]);

  return [optimisticState, setOptimisticState];
}
